import Cookies from 'js-cookie';

import DataDogLogs from 'utils/DataDogLogs';
import OneTrust from 'utils/OneTrust';
import Settings from 'utils/Settings';

import {
  STOREFRONT_API_VERSION,
  SHOPIFY_COOKIES,
  SHOPIFY_HEADERS,
  CONTENT_TYPE_APPLICATION_JSON,
} from './constants';
class Storefront {
  static get config() {
    return {
      host: Settings.shopPermanentDomain,
      storefrontAccessToken: Settings.storefrontAccessToken,
      storefrontApiVersion: STOREFRONT_API_VERSION,
    };
  }

  static abortController;

  static isDocumentHidden = () => {
    return document.hidden;
  };

  static call = async ({ operationName, query, variables }) => {
    if (Request && Request.prototype.hasOwnProperty('signal')) {
      Storefront.abortController = new AbortController();
      Storefront.addEventListeners();
    }

    const {
      host = null,
      storefrontAccessToken = null,
      storefrontApiVersion = null,
    } = Storefront.config;

    const method = 'POST';
    const scheme = 'https';
    const path = `/api/${storefrontApiVersion}/graphql.json`;
    const url = `${scheme}://${host}${path}`;
    const body = JSON.stringify({ query, variables, operationName });
    const shopify_y_cookie = Cookies.get(SHOPIFY_COOKIES.SHOPIFY_Y);
    const shopify_s_cookie = Cookies.get(SHOPIFY_COOKIES.SHOPIFY_S);
    const trackingHeaders = OneTrust.hasPerformanceConsent
      ? {
          ...(shopify_y_cookie
            ? { [SHOPIFY_HEADERS.SHOPIFY_STOREFRONT_Y]: shopify_y_cookie }
            : null),
          ...(shopify_s_cookie
            ? { [SHOPIFY_HEADERS.SHOPIFY_STOREFRONT_S]: shopify_s_cookie }
            : null),
        }
      : {};
    const headers = {
      [SHOPIFY_HEADERS.CONTENT_TYPE]: CONTENT_TYPE_APPLICATION_JSON,
      [SHOPIFY_HEADERS.X_SHOPIFY_STOREFRONT_ACCESS_TOKEN]: storefrontAccessToken,
      ...trackingHeaders,
    };
    const loggerFields = {
      operationName,
      http: {
        url,
        method,
        url_details: {
          host,
          scheme,
        },
      },
    };

    let endTime, duration;

    const startTime = Date.now();

    let additionalLoggerFields = {
      variables,
    };

    try {
      if (!storefrontAccessToken) {
        throw new Error('Missing storefrontAccessToken');
      }

      if (!storefrontApiVersion) {
        throw new Error('Missing storefrontApiVersion');
      }

      let response;
      let json;

      try {
        response = await fetch(url, {
          method,
          headers,
          body,
          ...(Storefront.abortController ? { signal: Storefront.abortController.signal } : {}),
        });

        json = await response.json();
      } catch (error) {
        throw error;
      }

      let responseHeaders = {};

      for (const [key, value] of response.headers.entries()) {
        responseHeaders[key] = value;
      }

      endTime = Date.now();
      duration = endTime - startTime;

      additionalLoggerFields = {
        ...additionalLoggerFields,
        ...(json?.data?.[operationName]?.userErrors?.length
          ? { userErrors: json.data[operationName].userErrors }
          : {}),
      };

      DataDogLogs.info(`Storefront:call - ${operationName} (${duration}ms)`, {
        duration,
        ...loggerFields,
        ...additionalLoggerFields,
        http: {
          ...loggerFields.http,
          ...(response.status ? { status_code: response.status } : {}),
        },
        shopify: {
          ...responseHeaders,
        },
      });

      /**
       * 200 error responses are an array of objects
       * 4xx and 5xx status code responses are an object with a "query" key
       * https://shopify.dev/docs/api/storefront#status_and_error_codes
       */

      if (json.errors) {
        if (Array.isArray(json.errors)) {
          throw new Error(JSON.stringify(json.errors));
        }
        if (json.errors.query) {
          throw new Error(json.errors.query);
        }
      }

      return json.data[operationName];
    } catch (error) {
      endTime = Date.now();
      duration = endTime - startTime;

      DataDogLogs.error(
        `Storefront:call ${operationName} - ${
          error.name === 'AbortError' ? 'User Aborted' : 'Failed'
        }`,
        {
          duration,
          ...loggerFields,
          ...additionalLoggerFields,
        },
        error
      );
      throw error;
    } finally {
      Storefront.removeEventListeners();
    }
  };

  static addEventListeners() {
    document.addEventListener('visibilitychange', Storefront.handleVisibilityChange);
    window.addEventListener('beforeunload', Storefront.handleUnload);
  }

  static removeEventListeners() {
    Storefront.abortController = null;
    document.removeEventListener('visibilitychange', Storefront.handleVisibilityChange);
    window.removeEventListener('beforeunload', Storefront.handleUnload);
  }

  static handleVisibilityChange = () => {
    if (Storefront.isDocumentHidden() && Storefront.abortController) {
      Storefront.abortController.abort();
    }
  };

  static handleUnload = () => {
    if (Storefront.abortController) {
      Storefront.abortController.abort();
    }
  };
}

export default Storefront;
